import React, { useMemo, useState } from "react";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import ArbitrageHero from "./components/ArbitrageHero";
import ArbitrageQuizPanel from "./components/ArbitrageQuizPanel";
import ArbitrageSectionCard from "./components/ArbitrageSectionCard";
import ArbitrageStats from "./components/ArbitrageStats";
import ArbitrageToolbar from "./components/ArbitrageToolbar";

import {
  ARBITRAGE_MODES,
  ARBITRAGE_SECTIONS,
  ARBITRAGE_STATS,
} from "./utils/arbitrageJudoData";

function ArbitrageJudoPage() {
  useScrollToTop();

  const [activeMode, setActiveMode] = useState("arbitrage");
  const [search, setSearch] = useState("");

  const visibleSections = useMemo(() => {
    const normalizedSearch = search
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

    return ARBITRAGE_SECTIONS.filter((section) => {
      const matchMode = section.mode === activeMode;

      if (!normalizedSearch) return matchMode;

      const searchableContent = [
        section.title,
        section.subtitle,
        section.description,
        ...(section.bullets || []),
        ...(section.rules || []).flatMap((rule) => [
          rule.title,
          ...(rule.paragraphs || []),
          ...(rule.bullets || []),
        ]),
      ]
        .join(" ")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

      return matchMode && searchableContent.includes(normalizedSearch);
    });
  }, [activeMode, search]);

  const activeModeLabel =
    ARBITRAGE_MODES.find((mode) => mode.value === activeMode)?.label ||
    "Arbitrage";

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <ArbitrageHero activeModeLabel={activeModeLabel} />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
              Arbitrage Judo
            </div>

            <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
              Règlement, commissaires sportifs & quiz
            </h1>

            <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
              Une seule page pour réviser les commandes de l’arbitre, les
              points, les pénalités, le rôle des commissaires sportifs et tester
              ses connaissances avec un quiz.
            </p>
          </div>

          <ArbitrageStats stats={ARBITRAGE_STATS} />
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <ArbitrageToolbar
          modes={ARBITRAGE_MODES}
          activeMode={activeMode}
          search={search}
          resultsCount={visibleSections.length}
          onModeChange={setActiveMode}
          onSearchChange={setSearch}
          onReset={() => {
            setActiveMode("arbitrage");
            setSearch("");
          }}
        />

        {activeMode === "quiz" ? (
          <ArbitrageQuizPanel />
        ) : visibleSections.length > 0 ? (
          <div className="mt-6 space-y-6">
            {visibleSections.map((section) => (
              <ArbitrageSectionCard key={section.id} section={section} />
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-[2rem] border border-dashed border-slate-300 bg-white p-8 text-center shadow-xl shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
            <h2 className="font-titre text-2xl font-bold text-slate-950 dark:text-white">
              Aucun contenu trouvé
            </h2>

            <p className="mx-auto mt-3 max-w-xl text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
              Essaie une autre recherche ou change d’onglet.
            </p>
          </div>
        )}
      </section>

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default ArbitrageJudoPage;